import React, { useState, useEffect } from 'react';
import { Database, Trash2, RefreshCw, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { saveBackupToFile, deleteOldBackups } from '../lib/backup';
import axios from 'axios';

interface BackupFile {
  name: string;
  url: string;
  date: string;
  size?: string;
}

// Maximum number of retry attempts
const MAX_RETRY_ATTEMPTS = 3;

// Maximum number of backup files to keep
const MAX_BACKUPS = 5;

// Function to implement exponential backoff delay
const getRetryDelay = (attempt: number) => Math.min(2 ** attempt * 1000, 10000);

// Get the backup API URL from environment variables with fallback
const BACKUP_API_BASE_URL = import.meta.env.VITE_BACKUP_API_URL || 'https://www.lotismode.com/backup';

export function BackupPage() {
  const [loading, setLoading] = useState(false);
  const [backups, setBackups] = useState<BackupFile[]>([]);
  const [loadingBackups, setLoadingBackups] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    filename: string;
    confirmText: string;
  }>({
    show: false,
    filename: '',
    confirmText: ''
  });

  useEffect(() => {
    loadBackups();
  }, []);

  const loadBackups = async (attempt = 0) => {
    try {
      if (attempt === 0) {
        setLoadingBackups(true);
        setLoadError(null);
      }
      
      console.log(`Chargement des sauvegardes... (tentative ${attempt + 1}/${MAX_RETRY_ATTEMPTS + 1})`);
      console.log(`Utilisation de l'URL: ${BACKUP_API_BASE_URL}/list.php`);
      
      // Direct request to list.php with specific accept headers
      const response = await axios.get(`${BACKUP_API_BASE_URL}/list.php`, {
        timeout: 15000,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Expires': '0',
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Réponse du serveur:', response.data);
      
      if (!Array.isArray(response.data)) {
        console.error('Format de réponse inattendu:', response.data);
        toast.error('Format de réponse inattendu lors du chargement des sauvegardes');
        setBackups([]);
        return;
      }
      
      const sqlBackups = response.data
        .filter((backup: BackupFile) => backup.name && backup.name.endsWith('.sql'))
        .sort((a: BackupFile, b: BackupFile) => 
          new Date(b.date).getTime() - new Date(a.date).getTime()
        );
      
      console.log('Sauvegardes SQL trouvées:', sqlBackups.length);
      
      // Don't limit the number of backups shown
      setBackups(sqlBackups);
      setRetryCount(0);
      setIsRetrying(false);
      setLoadError(null);
      
    } catch (error: any) {
      console.error(`Erreur lors du chargement des sauvegardes (tentative ${attempt + 1}):`, error);
      
      // Provide more detailed error messages based on error type
      let errorMessage = 'Erreur de connexion au serveur';
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        errorMessage = `Erreur de serveur: ${error.response.status} - ${error.response.statusText}`;
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = 'Le serveur de sauvegarde est inaccessible. Vérifiez votre connexion réseau ou contactez l\'administrateur.';
      } else {
        // Something happened in setting up the request that triggered an Error
        errorMessage = error.message || 'Erreur lors de la connexion au serveur de sauvegarde';
      }
      
      setLoadError(errorMessage);
      
      // If we haven't reached the maximum retry attempts, schedule a retry with exponential backoff
      if (attempt < MAX_RETRY_ATTEMPTS) {
        const nextAttempt = attempt + 1;
        const delay = getRetryDelay(nextAttempt);
        
        console.log(`Nouvelle tentative dans ${delay}ms (${nextAttempt}/${MAX_RETRY_ATTEMPTS})`);
        setIsRetrying(true);
        setRetryCount(nextAttempt);
        
        setTimeout(() => {
          loadBackups(nextAttempt);
        }, delay);
      } else {
        setIsRetrying(false);
        toast.error(`Impossible de charger les sauvegardes après ${MAX_RETRY_ATTEMPTS + 1} tentatives: ${errorMessage}`);
        setBackups([]);
      }
    } finally {
      if (attempt === MAX_RETRY_ATTEMPTS || !isRetrying) {
        setLoadingBackups(false);
      }
    }
  };

  const createBackup = async () => {
    try {
      setLoading(true);
      console.log('Création d\'une nouvelle sauvegarde...');
      
      // Check if we need to delete older backups first
      if (backups.length >= MAX_BACKUPS) {
        console.log(`Limite de ${MAX_BACKUPS} sauvegardes atteinte, suppression des anciennes sauvegardes...`);
        const backupsToDelete = backups.slice(MAX_BACKUPS - 1);
        const filesToDelete = backupsToDelete.map(backup => backup.name);
        
        if (filesToDelete.length > 0) {
          await deleteOldBackups(filesToDelete, BACKUP_API_BASE_URL);
          toast.success(`${filesToDelete.length} anciennes sauvegardes supprimées`);
        }
      }
      
      // Update to use the environment variable base URL
      const result = await saveBackupToFile(BACKUP_API_BASE_URL);
      
      if (result.success) {
        toast.success('Sauvegarde effectuée avec succès');
        // Attendre un peu avant de recharger la liste pour laisser le temps au serveur de traiter
        setTimeout(() => {
          loadBackups();
        }, 2000);
      } else {
        throw new Error('Erreur lors de la sauvegarde');
      }
    } catch (error: any) {
      console.error('Erreur lors de la création de la sauvegarde:', error);
      toast.error(error.message || 'Erreur lors de la création de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const initiateDelete = (filename: string) => {
    setDeleteConfirm({
      show: true,
      filename,
      confirmText: ''
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.confirmText.toLowerCase() !== 'oui') {
      toast.error('Veuillez écrire "oui" pour confirmer la suppression');
      return;
    }

    try {
      console.log('Suppression de la sauvegarde:', deleteConfirm.filename);
      // Update to use the environment variable base URL
      const cleanUrl = `${BACKUP_API_BASE_URL}/delete.php`.replace(/([^:]\/)\/+/g, "$1");
      
      await axios.post(cleanUrl, { 
        filename: deleteConfirm.filename 
      }, {
        timeout: 10000  // Augmenter le timeout à 10 secondes
      });
      toast.success('Sauvegarde supprimée');
      loadBackups();
      setDeleteConfirm({ show: false, filename: '', confirmText: '' });
    } catch (error: any) {
      console.error('Erreur lors de la suppression:', error);
      toast.error(`Erreur lors de la suppression: ${error.message || 'Erreur de connexion au serveur'}`);
    }
  };

  const handleDownloadBackup = (backupUrl: string, fileName: string) => {
    // Make sure the URL doesn't have double slashes
    const cleanUrl = backupUrl.replace(/([^:]\/)\/+/g, "$1");
    console.log(`Téléchargement démarré: ${cleanUrl}`);
    
    // Create an anchor element and set the href to the backup URL
    const a = document.createElement('a');
    a.href = cleanUrl;
    a.download = fileName; // Set the file name
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('Téléchargement démarré');
  };

  const handleManualRetry = () => {
    setRetryCount(0);
    loadBackups(0);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">Sauvegardes de la base de données</h2>
            <span className="text-sm text-gray-500">
              {backups.length} sauvegarde(s)
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center mb-8">
          <Database className="w-16 h-16 text-blue-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            Créer une nouvelle sauvegarde
          </h3>
          <p className="text-gray-600 mb-2">
            Sauvegarder toute la base de données dans un fichier SQL
          </p>
          <button
            onClick={createBackup}
            disabled={loading}
            className="bg-blue-500 text-white px-6 py-3 rounded-md hover:bg-blue-600 transition-colors flex items-center space-x-2 mx-auto disabled:opacity-50"
          >
            <Database className="w-5 h-5" />
            <span>
              {loading 
                ? 'Sauvegarde en cours...' 
                : 'Créer une sauvegarde'
              }
            </span>
          </button>
          <p className="text-sm text-gray-500 mt-2">
            Maximum {MAX_BACKUPS} sauvegardes - Les anciennes seront automatiquement supprimées
          </p>
        </div>

        <div className="border-t pt-8">
          <h3 className="text-lg font-semibold mb-4">Historique des sauvegardes</h3>
          {loadingBackups ? (
            <div className="text-center py-8">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-2"></div>
                {isRetrying && (
                  <p className="text-sm text-gray-500">
                    Tentative de connexion {retryCount}/{MAX_RETRY_ATTEMPTS}...
                  </p>
                )}
              </div>
            </div>
          ) : loadError ? (
            <div className="bg-red-50 p-4 rounded-lg mb-4 text-center">
              <p className="text-red-600 mb-2">Erreur lors du chargement des sauvegardes:</p>
              <p className="text-sm text-red-500 mb-4">{loadError}</p>
              <button
                onClick={handleManualRetry}
                className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                <span>Réessayer</span>
              </button>
            </div>
          ) : backups.length === 0 ? (
            <p className="text-center text-gray-500">Aucune sauvegarde disponible</p>
          ) : (
            <div className="space-y-4">
              {backups.map((backup) => (
                <div
                  key={backup.name}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium">{backup.name}</p>
                    <div className="flex space-x-4">
                      <p className="text-sm text-gray-500">
                        {new Date(backup.date).toLocaleString('fr-FR')}
                      </p>
                      {backup.size && <p className="text-sm text-gray-500">{backup.size}</p>}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {deleteConfirm.show && deleteConfirm.filename === backup.name ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          placeholder='Écrivez "oui" pour confirmer'
                          value={deleteConfirm.confirmText}
                          onChange={(e) => setDeleteConfirm(prev => ({
                            ...prev,
                            confirmText: e.target.value
                          }))}
                          className="border rounded px-2 py-1 text-sm"
                        />
                        <button
                          onClick={handleDeleteConfirm}
                          className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                        >
                          Confirmer
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ show: false, filename: '', confirmText: '' })}
                          className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600"
                        >
                          Annuler
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => handleDownloadBackup(backup.url, backup.name)}
                          className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          <span>Télécharger</span>
                        </button>
                        <button
                          onClick={() => initiateDelete(backup.name)}
                          className="inline-flex items-center px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          <span>Supprimer</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}